/****************
 * What happened to a copied order — the shared vocabulary.
 *
 * ⚠️ THIS FILE EXISTS TWICE, BYTE FOR BYTE:
 *      frontend/src/lib/orderOutcome.core.ts
 *      mobile-app/src/lib/orderOutcome.core.ts
 * They are compared by a Go test (model/filtercode_contract_test.go), which
 * fails if they drift. Edit one, copy it to the other, run the test. It has no
 * imports on purpose: the moment it depends on a Tailwind class or a React
 * Native colour token, the two copies can no longer be identical. Styling lives
 * in each app's own orderOutcome.ts beside it.
 *
 * Before 2026-09-02 the two apps each carried their OWN copy of this logic and
 * had already drifted: the phone still said "Filtered by your strategy —
 * nothing went wrong" for orders that hit our own bug. That is what this file
 * is here to prevent.
 *
 * THE ORDER OF AUTHORITY, most trusted first:
 *   1. filter_code — a value the backend writes (model/filtercode.go). Stable.
 *   2. filter_reason / last_error prose — matched by regex, for rows written
 *      before filter_code existed, and for a server newer than this client.
 *   3. execution_status.
 *
 * There are several reasons an order does not reach the exchange and they mean
 * very different things — the asset is not listed on the broker; the user's own
 * rules excluded it; there was not enough money; there was no position to act
 * on; the session had stopped; a setting was never filled in; or we hit a bug.
 * Only some of those involve the BROKER. A user asking "why didn't my order go
 * through" needs to know which half they are in, and being told "your own rules
 * excluded it" when the truth is "our bug" or "your session had stopped" sends
 * them to the strategy builder to fix something that is not there.
 ****************/

// >>> GENERATED FROM backend/internal/services/replicator/model/filtercode.go
// Do not edit by hand. Regenerate with:
//   go test ./internal/services/replicator/model -run TestFilterCode -update
export type FilterCode =
  | 'action_ignored'
  | 'direction_blocked'
  | 'internal_error'
  | 'leverage_above_cap'
  | 'margin_cap'
  | 'market_closed'
  | 'max_trades'
  | 'no_position'
  | 'session_paused'
  | 'position_predates_us'
  | 'trader_already_left'
  | 'set_disagrees'
  | 'duplicate_close'
  | 'session_stopped'
  | 'setting_missing'
  | 'size_too_small'
  | 'symbol_blocked'
  | 'symbol_unsupported'
  | 'wallet_empty';

export const ALL_FILTER_CODES: FilterCode[] = [
  'action_ignored',
  'direction_blocked',
  'internal_error',
  'leverage_above_cap',
  'margin_cap',
  'market_closed',
  'max_trades',
  'no_position',
  'session_paused',
  'position_predates_us',
  'trader_already_left',
  'set_disagrees',
  'duplicate_close',
  'session_stopped',
  'setting_missing',
  'size_too_small',
  'symbol_blocked',
  'symbol_unsupported',
  'wallet_empty',
];
// <<< GENERATED

export type Outcome =
  /**
   * A session started or ended. Not an order at all -- it rides in the order
   * feed because that is what the notification bell reads, and Guillaume asked
   * for "a notif when a session start or end and why it ended". Before this
   * there was no session-lifecycle row anywhere in the feed, so the bell could
   * not show one however long you waited.
   */
  | 'lifecycle'
  | 'filled'
  /**
   * The broker has the order and it has not filled.
   *
   * Guillaume, 2026-09-16: "I see the Decrease order listed as 'Executed' in my
   * order list of the Session" -- on four DECREASE orders sitting at
   * `submitted`, whose money had not been booked anywhere, because
   * `submitted` was folded into `filled` one line below and rendered
   * "Executed".
   *
   * They are different facts and the difference is the user's money. An
   * acknowledgement means the venue accepted the instruction; a fill means it
   * happened at a price, for a size, with a fee. The backend has never confused
   * the two -- "an order is only a fill if the broker says filled" -- and the
   * client said Executed for both.
   *
   * Not folded into `pending` either: pending is an order we have not placed,
   * and a resting limit order the exchange is holding is a thing the user can
   * see on Phemex and act on.
   */
  | 'working'
  | 'signalled'
  | 'pending'
  | 'notListed'
  | 'insufficientFunds'
  | 'noPosition'
  | 'strategy'
  /**
   * The session was paused when the trade came through.
   *
   * Its own outcome rather than a shade of `killSwitch` or `strategy`. A kill
   * switch ENDED a session on a safety rule; a strategy rule is the user's own
   * configuration turning a trade down. A pause is neither -- it is the user
   * deliberately holding, with every intention of resuming, and the trade is
   * gone only because they were not listening at that moment.
   *
   * Told apart in the UI so a paused session's skipped orders read as an
   * expected consequence of a button the user pressed, not as a fault.
   */
  | 'paused'
  | 'killSwitch'
  | 'needsSetting'
  | 'ourFault'
  /**
   * A record the books have already settled and set aside.
   *
   * Two backend statuses land here. `superseded` is a second filled close on a
   * trade that was already closed -- real, and deliberately excluded from every
   * total so the position is not counted out twice. `voided` is a row withdrawn
   * for the same reason.
   *
   * Its own outcome because it was neither: it is not pending (it happened, and
   * nothing is waiting), and it is not failed (nothing went wrong). Guillaume,
   * 2026-09-12, on session 281: "My live session have 2 pending and 2 failed
   * orders, what does that mean?" It meant these four rows, and the honest
   * answer was that both labels were wrong.
   */
  | 'settled'
  /**
   * THE VENUE'S OWN REASONS. Sub-categories of Failed, not siblings of it.
   *
   * Guillaume, 2026-09-14: "Filtered and Failed are the 2 category but they
   * should have sub-category... Filtered by which strategy rule, Failed by
   * the venue's reason. Missed entries from OI should not be a category, it
   * should be a sub-category of Failed."
   *
   * Each of these counts as Failed in summariseOutcomes. They exist apart
   * from the plain 'failed' so the row can say WHICH refusal it was, and
   * apart from insufficientFunds and friends because those mean we never
   * sent the order. A refusal reached the exchange and came back; telling a
   * user their own rules stopped it would be false.
   */
  | 'traderLeftBeforeItFilled'
  /** The user cancelled it themselves, from the tray, while it waited. */
  | 'cancelledByYou'
  | 'acceptedButNeverFilled'
  /** OUR refusal: the size was under the broker's smallest order, so nothing was sent. */
  | 'tooSmallNotSent'
  /** A close the broker had no position for, with no earlier close of ours behind it. */
  | 'nothingToClose'
  /** The broker had trading switched off on this contract. */
  | 'venueTradingOff'
  | 'venueNoMargin'
  | 'venueOrderTooSmall'
  | 'venueStopBeyondLiquidation'
  /**
   * A stop or target whose trigger the price had already reached when the
   * order arrived: it would have fired at once, so the exchange would not
   * hold it. Phemex 11044 (a long's stop, a short's target) and 11043 (a
   * short's stop, a long's target). 2026-10-01: three of these on 09-30 fell
   * to 'failed' and read "Your broker refused it" with a bare number below.
   */
  | 'venueTriggerAlreadyReached'
  /**
   * The exchange capped total open interest on the contract and is taking
   * only closing orders. Nothing is wrong with the account, nothing the
   * user can change, and it clears by itself -- which is exactly why it
   * needs its own words rather than sharing 'failed'.
   */
  | 'venuePaused'
  | 'failed';

/**
 * Every code the backend can write, mapped to what the user is told.
 *
 * This is a Record over the full union, so a code added in Go and regenerated
 * into the block above makes this object a type error until it is given a
 * meaning here. That is the point: the alternative is a silent fall-through to
 * "your rules skipped it", which is how a user ends up being blamed for our bug.
 *
 * Several codes deliberately share an outcome. margin_cap, max_trades,
 * symbol_blocked, direction_blocked and action_ignored are all genuinely the
 * user's own rules working as set; the prose beside them says WHICH one.
 */
export const CODE_OUTCOME: Record<FilterCode, Outcome> = {
  action_ignored: 'strategy',
  direction_blocked: 'strategy',
  internal_error: 'ourFault',
  // Their own leverage cap: the symbol is already open above it (Groups & Categories phase 2, 10-26).
  leverage_above_cap: 'strategy',
  margin_cap: 'strategy',
  max_trades: 'strategy',
  // Their own "don't open while the market is closed" rule (2026-10-06).
  market_closed: 'strategy',
  no_position: 'noPosition',
  // The trader already held it before we saw them: a position we discovered,
  // not a trade they made. Same meaning as no_position for the reader — there
  // was no opening trade to copy — and emphatically not the user's rules.
  position_predates_us: 'noPosition',
  // The trader closed before his entry reached us (a late row): copying it
  // would leave a position with no exit to follow. Not the user's rules (E1, 10-31).
  trader_already_left: 'traderLeftBeforeItFilled',
  // Another close for this position was already sent and is still working.
  // settled, not failed: nothing went wrong and the position IS being closed
  // -- by the order that got there first. It is set aside for the same reason
  // a superseded close is, so the position is never counted out twice.
  duplicate_close: 'settled',
  session_paused: 'paused',
  session_stopped: 'killSwitch',
  // Their own rule on one of their groups (phase 4): the group did not agree enough.
  set_disagrees: 'strategy',
  setting_missing: 'needsSetting',
  size_too_small: 'insufficientFunds',
  symbol_blocked: 'strategy',
  symbol_unsupported: 'notListed',
  wallet_empty: 'insufficientFunds',
};

/**
 * The venue's refusal tokens, mapped to what the user is told.
 *
 * Written by the backend into replicated_orders.failure_code from a single
 * table in phemex/errors.go, so the sentence a user reads and the bucket this
 * puts them in cannot disagree about the same refusal.
 *
 * Codes share an outcome where the distinction is not the user's business:
 * 11001 and 11082 are both 'the account cannot cover this', 11055 and 11058
 * are both 'below a broker minimum'. The prose on the row says which.
 *
 * A token this client has never seen falls back to the prose, exactly as
 * classifyStop does, rather than guessing.
 */
export const FAILURE_OUTCOME: Record<string, Outcome> = {
  TE_NO_ENOUGH_AVAILABLE_BALANCE: 'venueNoMargin',
  TE_CANNOT_COVER_ESTIMATE_ORDER_LOSS: 'venueNoMargin',
  TE_PRICE_TOO_SMALL: 'venueOrderTooSmall',
  TE_QTY_TOO_SMALL: 'venueOrderTooSmall',
  TE_BUY_SL_SHOULD_GT_LIQ: 'venueStopBeyondLiquidation',
  // 11051, the same refusal on a short (2026-10-06: stops now go out exactly as
  // set, so a short's stop past liquidation reaches the exchange too).
  TE_SELL_SL_SHOULD_LT_LIQ: 'venueStopBeyondLiquidation',
  // 11044 and 11043: one event to the user, whichever side the position is on.
  TE_FALLING_TRIGGER_DIRECTLY: 'venueTriggerAlreadyReached',
  TE_RISING_TRIGGER_DIRECTLY: 'venueTriggerAlreadyReached',
  TE_OI_LIMIT_REDUCE_ONLY: 'venuePaused',
  /*
   * 11140 (2026-10-06): our reduce, refused whole for carrying the session's
   * stop or target. Plain 'failed' -- it reached the exchange and was turned
   * down, and the row's own sentence says why, that nothing was reduced, and
   * that CopyFi attached the stop. Mapped rather than left to the prose, so a
   * rewording of that sentence can never move it into another bucket.
   */
  TE_POS_REDUCE_CANNOT_ATTACH_TP_SL: 'failed',
  // 11054 (2026-10-07): a closing order or stop priced past the position's
  // bankruptcy price. It reached the exchange and was turned down: 'failed',
  // with the row's own sentence saying why.
  TE_PRICE_WORSE_THAN_BANKRUPT: 'failed',
  /**
   * Nothing left to reduce: the position had already closed. Not a failure
   * the user should be shown as one -- the close they wanted happened.
   *
   * 'settled', not 'noPosition'. This comment said the right thing for as long
   * as the mapping below it said the wrong one: 'noPosition' tells the user
   * "a position you did not open ... that was never open", which is false here
   * -- it WAS open, and our own close reached the venue a second first. It is
   * the second of Guillaume's "double closing orders", 2026-09-18: the real
   * close reads Executed and this one, beneath it, read as a position he
   * never had. 'settled' says what happened: the first close did it, and this
   * row is counted in nothing.
   */
  TE_REDUCE_ONLY_ABORT: 'settled',
  /**
   * OURS, not the venue's.
   *
   * Every other token here is something Phemex said. This one is written by
   * broker_reconciler when an order was accepted, never confirmed, and the
   * broker's own trade history shows no fill for it. Without it the row falls
   * to the catch-all `failed` -- "your broker turned it down, their reason is
   * below" -- and both halves are false, which is what Guillaume read on his
   * XLM order on 2026-09-15.
   */
  CF_NO_FILL_IN_HISTORY: 'acceptedButNeverFilled',
  /**
   * OURS. The trader closed the position while our entry was still resting
   * on the venue's book, so we cancelled it rather than let it open a
   * position they had already left. Written by cancelAbandonedEntries --
   * the first code path in this project that has ever cancelled an order.
   */
  CF_TRADER_EXITED_BEFORE_FILL: 'traderLeftBeforeItFilled',
  /*
   * 2026-09-19: the tray's Cancel button. Without this entry the order fell to
   * classifyProse and read "Failed" -- a cancel the user asked for, shown as
   * the broker refusing them.
   */
  CF_CANCELLED_BY_USER: 'cancelledByYou',
  /*
   * 2026-09-19 (#7), from a census of 1,309 real orders. Each of these used
   * to fall to 'failed' -- "your broker turned it down" -- on an order the
   * broker never received, or read as a duplicate close that never was.
   * The backend serves the code for old rows too (applySituationCodes).
   */
  CF_BELOW_MIN_LOT: 'tooSmallNotSent',
  CF_NO_PRICE: 'ourFault',
  CF_NOTHING_TO_CLOSE: 'nothingToClose',
  // 2026-11-03: the broker's market is shut. A close is held and retried when it reopens (the row's own
  // sentence says so); a refused open is a plain refusal.
  CF_MARKET_CLOSED: 'failed',
  TE_SEQ_TURN_OFF: 'venueTradingOff',
  'Symbol not listed': 'notListed',
};

export interface ReplicatedOrderLike {
  id: number;
  subscription_id?: number;
  symbol?: string;
  direction?: string;
  master_action_type?: string;
  adjusted_quantity?: number;
  execution_status?: string;
  was_filtered?: boolean;
  /** Written since 2026-09-02. Blank on every older row. */
  filter_code?: string | null;
  filter_reason?: string | null;
  last_error?: string | null;
  /**
   * The venue's own token for a refusal. Written since 2026-09-14; blank on
   * every older row, which is why the prose fallback below stays.
   */
  failure_code?: string | null;
  /**
   * MARKET, LIMIT, STOP_LOSS, TAKE_PROFIT, LIQUIDATION. Served as
   * inferred_order_type; the name here is the field, not the inference.
   */
  inferred_order_type?: string | null;
  /**
   * A paper order, or any order of a paper session (the caller sets it from
   * the session: rows the engine mis-routed carry is_paper false).
   */
  is_paper?: boolean;
  created_at?: string;
}

/**
 * The fallback, for rows written before filter_code existed and for the window
 * after a backend deploy when the server knows a code this client does not.
 * Matched against the strings the engine actually writes — fanout.go, filter.go,
 * sizing.go and executor.go, not one of them alone. Anything unmatched falls
 * through to 'strategy', which is the honest default: that is where the
 * genuinely user-configured rules live.
 */
const LEGACY_FILTER_PATTERNS: { test: RegExp; outcome: Outcome }[] = [
  // "%s is not listed on Phemex — this trade can't be replicated"
  { test: /not listed on|not available on|unsupported symbol/i, outcome: 'notListed' },

  // fanout.go — "%s skipped: no open replicated position for %s (...)"
  // sizing.go — "Decrease liquidateAll: no current position", "No tracked
  // position to close". The last two used to fall through to 'strategy' and
  // told a user their own rules excluded an order when in fact there was
  // nothing to act on.
  {
    test: /no open replicated position|no matching position|never opened|no current position|no tracked position/i,
    outcome: 'noPosition',
  },

  // "Effective wallet size is zero or negative", "Adjusted quantity is zero or
  // negative", and Phemex's own "insufficient balance: need $X ...".
  {
    test: /wallet size is zero|quantity is zero|insufficient|not enough (funds|margin|balance)|balance too low/i,
    outcome: 'insufficientFunds',
  },

  // "Kill switch triggered — session stopped", "subscription no longer active
  // at execution time", "Session stopped before this order ...". All mean the
  // session was not running. The first read as "nothing went wrong", which is
  // the opposite of true; the second read as a broker refusal for an order the
  // broker never received.
  {
    test: /kill switch|subscription no longer active|session (no longer|not) active|session stopped/i,
    outcome: 'killSwitch',
  },

  // "Increase fixedAmount: no value configured", "Decrease fixedPortion: no
  // value configured", "Trader wallet balance unknown and no fallback
  // percentage set". A setting is missing rather than a rule having fired. The
  // user CAN fix these, but not by hunting for the rule that excluded them.
  {
    test: /no value configured|no fallback percentage|wallet (balance )?unknown/i,
    outcome: 'needsSetting',
  },

  // "Unknown action type: X", "Unknown increase strategy X", "Cannot size order
  // for margin cap". Ours, not theirs.
  {
    test: /^unknown (action type|increase strategy|decrease strategy)|^cannot size order/i,
    outcome: 'ourFault',
  },
];

function classifyProse(reason: string | null | undefined): Outcome {
  if (!reason) return 'strategy';
  for (const pattern of LEGACY_FILTER_PATTERNS) {
    if (pattern.test.test(reason)) return pattern.outcome;
  }
  return 'strategy';
}

/** The code if we have one and know it, the prose otherwise. */
function classifyStop(
  code: string | null | undefined,
  reason: string | null | undefined,
): Outcome {
  if (code) {
    const known = CODE_OUTCOME[code as FilterCode];
    if (known) return known;
    // A code this client has never heard of means the server is ahead of the
    // bundle. Fall back to the prose rather than guessing, and never claim it
    // was the user's own rule.
  }
  return classifyProse(reason);
}

export function outcomeOf(order: ReplicatedOrderLike): Outcome {
  const status = (order.execution_status || '').toLowerCase();

  // A signal session's order: described, never placed. Before this it fell
  // through to 'pending', so a signal that had already been delivered read as
  // an order still waiting to happen — and a user looking for why they got
  // nothing found a page full of "Pending". 2026-09-03.
  // Before every other branch: a lifecycle row carries a filter_code (the stop
  // reason) and would otherwise be classified as a filtered ORDER and told the
  // user their rules skipped something.
  if (status === 'lifecycle') return 'lifecycle';

  if (status === 'signalled') return 'signalled';

  // A status of 'filtered' means the engine stopped it, whatever else is
  // attached. executor.go used to set exactly this status AND write a
  // last_error without setting was_filtered, so the last_error branch below
  // claimed the broker refused an order the broker never received.
  // was_filtered is not reliable on its own: updateRecord never writes it.
  if (status === 'filtered') {
    return classifyStop(order.filter_code, order.filter_reason || order.last_error);
  }

  /*
   * Settled and set aside, before the last_error branch below.
   *
   * The reconciler writes its own explanation into last_error -- "synthesized
   * CLOSE by broker reconciler, position closed on exchange" -- which is a note,
   * not an error. That branch reads ANY text in last_error as a failure, so two
   * successful closes on session 281 were counted as failed orders. The other
   * two of that session's four were `superseded` rows reaching the fallthrough
   * at the bottom of this function and being called pending, eight days after
   * they had finished.
   *
   * Above last_error because for these two statuses the field is a note. Below
   * `filtered`, which still outranks everything.
   */
  /*
   * A repeat of one trader action. When the first copy was stopped by a
   * rule, the repeat carries that rule, and saying "duplicate of a close"
   * on a skipped INCREASE was simply false (51 of Guillaume's orders).
   */
  if (status === 'superseded' && order.filter_code) {
    return classifyStop(order.filter_code, order.filter_reason);
  }
  if (status === 'superseded' || status === 'voided') return 'settled';
  // A close that arrived after the position was gone: it closed nothing.
  // It used to fall through to 'pending' -- "Pending" on a finished row.
  if (status === 'no_position') return 'noPosition';

  // Exchange failure outranks the filtered flag.
  //
  // Checking `was_filtered` first meant an order that reached the broker and
  // was rejected — venue down, pair not listed there — still displayed as
  // "filtered by strategy", blaming the user's configuration for an outage.
  if (status === 'failed' || status === 'rejected' || order.last_error) {
    /*
     * THE CODE BEFORE THE PROSE, for the same reason filter_code outranks
     * filter_reason: the token is written from one table and cannot drift,
     * and the prose is a sentence somebody may reword. Before this, every
     * refusal the exchange explained landed on a single 'failed' badge and
     * the user was left to read the broker's raw message for the reason.
     */
    /*
     * A PAPER ORDER HAS NO BROKER TO REFUSE IT. Guillaume, 2026-09-30, on
     * session 284: hundreds of failed orders on a paper session read "Your
     * broker refused it". Each was our own engine failing -- a paper close
     * routed to the live executor, which could not load a key that does not
     * exist ("record not found"). A paper failure is ours, so it says so; only
     * a reason the prose recognises (no wallet left, session stopped) keeps
     * its own badge.
     */
    if (order.is_paper) {
      const ours = classifyProse(order.last_error);
      return ours === 'strategy' ? 'ourFault' : ours;
    }
    if (order.failure_code) {
      const known = FAILURE_OUTCOME[order.failure_code];
      if (known) return known;
      // A token this bundle has not heard of: the server is ahead of the
      // client. Fall through to the prose rather than inventing a bucket.
    }
    // The broker's own words still say which kind of failure it was.
    const fromBroker = classifyProse(order.last_error);
    return fromBroker === 'strategy' ? 'failed' : fromBroker;
  }

  if (order.was_filtered) return classifyStop(order.filter_code, order.filter_reason);
  if (status === 'filled') return 'filled';
  // `submitted` is the broker acknowledging the instruction, not executing it.
  // Folding it into `filled` here is what labelled four unfilled DECREASE
  // orders "Executed" on Guillaume's session, 2026-09-16.
  if (status === 'submitted') return 'working';
  return 'pending';
}

/**
 * Phemex is the only broker today, but naming it in a constant guarantees the
 * label lies the day a second one exists — a MEXC user would be told their pair
 * is "not on Phemex". Pass the venue where the caller knows it.
 */
export const DEFAULT_BROKER = 'your broker';

/****************
 * "WORKING AT THE BROKER" DOES NOT SAY WHAT IS BEING WAITED FOR.
 *
 * Guillaume, 2026-09-20: a limit order "is just listed as Working at the
 * Broker, which I THINK it mean Limit Order Waiting to be filled". Having to
 * infer it is the defect: a market order that has not come back yet and a
 * limit order resting on the book are the same three words, and only one of
 * them is waiting on a price.
 *
 * The order type is on the row already. Passed through rather than guessed.
 ****************/
export function labelForOrder(
  order: ReplicatedOrderLike,
  outcome: Outcome,
  broker: string = DEFAULT_BROKER,
): string {
  if (outcome === 'working' &&
      (order.inferred_order_type || '').toUpperCase() === 'LIMIT') {
    return 'Limit order · waiting for execution';
  }
  return labelFor(outcome, broker);
}

/****************
 * A TICKER AND ONE SENTENCE, NOT THREE PARAGRAPHS.
 *
 * Guillaume, 2026-09-20: "The Status section should just be a ticker with the
 * status and a quick sentence explaining it, some have 2-3 paragraph, for a
 * beginner this is absolutely overwhelming, way to much text for too little
 * info."
 *
 * Nothing is deleted -- the full text is still on the row and the caller shows
 * it on hover. What changes is what is on screen without asking: the first
 * sentence, and a character cap for the writers that do not use full stops.
 *
 * The cap is generous enough for a real sentence and short enough that three
 * of them stacked still read as three lines rather than a wall.
 ****************/
export const DETAIL_MAX = 120;

export function shortDetail(text: string | null | undefined): string | null {
  if (!text) return null;
  const clean = text.replace(/\s+/g, ' ').trim();
  if (!clean) return null;

  // First sentence, when there is one and it is not the whole thing.
  const stop = clean.search(/[.!?](\s|$)/);
  let out = stop > 0 && stop + 1 < clean.length ? clean.slice(0, stop + 1) : clean;

  if (out.length > DETAIL_MAX) {
    // Cut on a word boundary rather than mid-word.
    const cut = out.lastIndexOf(' ', DETAIL_MAX);
    out = out.slice(0, cut > 40 ? cut : DETAIL_MAX).trimEnd() + '\u2026';
  }
  return out;
}

export function labelFor(outcome: Outcome, broker: string = DEFAULT_BROKER): string {
  switch (outcome) {
    case 'lifecycle':
      return 'Session';
    case 'filled':
      return 'Executed';
    // The trader's word for an order the exchange is holding. "Pending" would
    // read as something we had not sent yet, which is the opposite of true.
    case 'working':
      return 'Waiting for execution'; // Guillaume 10-25
    case 'signalled':
      return 'Signal sent';
    case 'pending':
      return 'Pending';
    case 'notListed':
      return `Not on ${broker}`;
    case 'insufficientFunds':
      return 'Not enough funds';
    // Covers OPEN, INCREASE, DECREASE and CLOSE. "Nothing to close" was wrong
    // for three of the four — an INCREASE on a position never opened is not a
    // close, and saying so sends people looking for a position that is not there.
    case 'noPosition':
      return 'No position to act on';
    case 'strategy':
      return 'Your rules skipped it';
    // The user's own word for it, deliberately. Guillaume asked for the reason
    // to read "Paused Session", and this is the label that carries it.
    case 'paused':
      return 'Paused Session';
    case 'killSwitch':
      return 'Your session was not running';
    case 'needsSetting':
      return 'A setting is missing';
    case 'ourFault':
      return 'Our problem, not yours';
    case 'settled':
      return 'Already settled';
    /*
     * NOT "refused". The venue accepted it and never filled it, and their own
     * trade history has no trade for it. Calling that a refusal sends the user
     * looking for a reason that does not exist.
     */
    /*
     * Not a failure and not a refusal. A rule of the user's own copying
     * worked: the trader left, so the order they were copying into stopped
     * being worth filling.
     */
    case 'traderLeftBeforeItFilled':
      return 'Cancelled — the trader left';
    case 'cancelledByYou':
      return 'Cancelled by you';
    case 'acceptedButNeverFilled':
      // M-72, 2026-09-25, Guillaume: an order that never filled reads "cancelled".
      return 'Cancelled — never filled';
    case 'tooSmallNotSent':
      return `Too small for ${broker} — not sent`;
    case 'nothingToClose':
      return `Nothing to close at ${broker}`;
    case 'venueTradingOff':
      return 'Refused: trading paused on this market';
    case 'failed':
      return 'Your broker refused it';
    /*
     * WHICH refusal, in the badge itself. 'Your broker refused it' was true
     * of all of these and useful about none: a user reading it had to open
     * the row and parse the exchange's own message to learn whether the
     * problem was their balance, their size, their stop, or nothing to do
     * with them at all.
     */
    case 'venueNoMargin':
      return 'Refused: not enough margin';
    case 'venueOrderTooSmall':
      return `Refused: below the ${broker} minimum`;
    case 'venueStopBeyondLiquidation':
      return 'Refused: stop past liquidation';
    case 'venueTriggerAlreadyReached':
      return 'Refused: price already at the trigger';
    case 'venuePaused':
      return 'Refused: new positions paused';
  }
}

/** One line saying what happened and whether it reached the broker. This is
 *  the part that stops a user hunting in the wrong place. */
export function explanationFor(
  outcome: Outcome,
  broker: string = DEFAULT_BROKER,
): string | null {
  switch (outcome) {
    // The row carries its own sentence -- "Kill switch: down 51.2% of $112.92,
    // past your 50.00% drawdown limit" -- so a generic one here would only get
    // in front of it.
    case 'lifecycle':
      return null;
    case 'signalled':
      return 'We sent you this order and placed nothing. It is yours to act on, or not.';
    case 'notListed':
      return `The trader traded a market ${broker} does not offer, so there was no way to copy it. Nothing was sent and nothing was charged.`;
    case 'insufficientFunds':
      return 'CopyFi did not send this one: the session\'s wallet had no money left to give it, or your share of this trade came out too small to place. Nothing reached your exchange.';
    case 'noPosition':
      return 'This order was made to adjust or close a position you did not open. We have filtered it out because we can’t act upon a position that was never open.';
    case 'strategy':
      return 'A rule you set turned this one down. That is the strategy working, not a fault. The exact rule is below.';
    case 'killSwitch':
      return 'The session had already stopped when this arrived, so nothing was copied. The session itself says why it ended — look for it in this list. Nothing reached your broker, and nothing else will copy until you start a new session.';
    case 'needsSetting':
      return 'Your strategy needs a value it does not have yet, so this one could not be sized. Open the strategy builder and fill in the setting named below.';
    case 'paused':
      return 'Your session was paused when this trade came through, so nothing was placed. Resume it and copying continues from the next trade — this one has already been and gone.';
    case 'settled':
      return 'This repeats an order that had already been handled, so it did nothing. It is kept for the record and counted in nothing: your PnL, your fees and your trade count all come from the first one. There is nothing to act on.';
    case 'tooSmallNotSent':
      return `Your size for this trade came out under the smallest order ${broker} accepts, so we did not send it. ${broker} never saw it and nothing was charged. The numbers are below.`;
    case 'nothingToClose':
      return `${broker} had no open position for this to close. Either it was never opened on your account, or it had already been closed another way (a stop-loss, a take-profit, or by hand). Nothing changed on your account.`;
    case 'venueTradingOff':
      return `${broker} had trading switched off on this contract when the order arrived, so it was refused. Nothing is wrong with your account, and anything already open is unaffected.`;
    case 'ourFault':
      return 'CopyFi could not handle this one. That is a bug on our side, not anything you configured. Nothing was sent to your account.';
    case 'cancelledByYou':
      return `You cancelled this order while it was still waiting on ${broker}. Nothing was opened.`;
    case 'traderLeftBeforeItFilled':
      return (
        'The trader you copy closed this position while your order was still ' +
        `waiting to be filled on ${broker}, so we cancelled it. Nothing was ` +
        'opened and nothing was spent — filling it would have opened a ' +
        'position they had already left.'
      );
    case 'acceptedButNeverFilled':
      return (
        `Cancelled. ${broker} accepted this order and then never filled it. We checked ` +
        `their own trade history and there is no trade for it, so nothing was ` +
        'opened and nothing was spent. This is not a refusal — there is no ' +
        `reason from ${broker} to show you, because they did not give one.`
      );
    case 'failed':
      return `It reached ${broker} and ${broker} turned it down. Their reason is below.`;
    case 'venueNoMargin':
      return (
        `We sent this to ${broker} and it was refused: the account could not cover ` +
        'it. Available margin is what is left after open positions and their fees, ' +
        'so a second position can fail while the first is still fine.'
      );
    case 'venueOrderTooSmall':
      return (
        `We sent this to ${broker} and it was refused for being below their ` +
        'minimum for this contract. A small wallet at high leverage can round ' +
        'down to less than one contract.'
      );
    case 'venueStopBeyondLiquidation':
      return (
        'The stop-loss sat beyond the price at which this position would be ' +
        'liquidated, so the exchange would not hold it: the liquidation would ' +
        'always have happened first and the stop could never have fired.'
      );
    case 'venueTriggerAlreadyReached':
      return (
        'The price had already reached the level this stop or target was set ' +
        'at when the order arrived, so it would have fired the moment it was ' +
        'placed and the exchange would not hold it. The position is open ' +
        'without the stop or target that was asked for.'
      );
    case 'venuePaused':
      return (
        `${broker} has paused NEW positions on this contract because the total ` +
        'open interest across everyone reached their cap. Nothing is wrong with ' +
        'your account, anything already open is unaffected, and it clears by ' +
        'itself as other traders close.'
      );
    default:
      return null;
  }
}

/**
 * The one thing the user should do next, or null when there is nothing to do.
 * Kept apart from the explanation so a UI can show it as an action.
 */
export function nextStepFor(outcome: Outcome): string | null {
  switch (outcome) {
    /*
     * The venue refusals, first, because these are the ones where the next
     * step is not obvious. venuePaused deliberately returns null: there is
     * nothing the user can do, and inventing an action for it would send
     * them to change a setting that was never the problem.
     */
    case 'venueNoMargin':
      return 'Your exchange did not have enough free margin for this order at its size and leverage. A smaller size per trade or a lower leverage cap makes each order need less.';
    case 'venueOrderTooSmall':
      return 'Raise the wallet share per trade: the order rounded down below the minimum size.';
    /*
     * "Widen your stop" said the opposite of the fix: a stop past liquidation
     * has to come CLOSER to the entry. Stated as what the exchange holds, not
     * as advice (2026-10-06: no suggestion comes from CopyFi).
     */
    case 'venueStopBeyondLiquidation':
      return 'Your exchange only holds a stop closer to your entry than the liquidation price. At these settings yours sits past it, so every copy opens without one until the stop or the leverage changes.';
    case 'venueTriggerAlreadyReached':
      return 'This position has no stop or target at the exchange. You can set one at a level the price has not reached yet, or close the position.';
    case 'venuePaused':
      return null;
    case 'tooSmallNotSent':
      return 'Give each trade more margin in your strategy, for example a higher Mirror Margin %, so trades like this reach the minimum.';
    case 'nothingToClose':
    case 'venueTradingOff':
      return null;
    case 'insufficientFunds':
      return 'Check the session\'s wallet on its page. A session whose wallet reached zero skips every new trade until a new session is started; a share that rounds to nothing needs a larger size per trade in your strategy.';
    case 'needsSetting':
      return 'A value this strategy needs is missing. If it sizes by Mirror margin %, the trader\'s wallet was unknown for this order and a different sizing method avoids it; otherwise, fill in the missing value in the Strategy Builder.';
    case 'paused':
      // Unlike a killed session, this one is still there and still holding
      // whatever it held. Resuming is the whole action.
      return 'Resume the session when you want it copying again.';
    case 'killSwitch':
      return 'Start a new session when you want copying to resume.';
    case 'strategy':
      return 'Change the rule in your strategy if you did not mean to exclude this.';
    case 'notListed':
    case 'noPosition':
    case 'ourFault':
    case 'traderLeftBeforeItFilled':
    case 'cancelledByYou':
    case 'acceptedButNeverFilled':
    case 'failed':
    case 'filled':
    case 'signalled':
    case 'pending':
    // Resting at the broker at its own price: it fills when the market gets
    // there, and asks nothing of the reader until then. Missing here, it fell
    // through and returned undefined -- harmless where the answer is only
    // tested for truth, and a compile error under the app's strict mode.
    case 'working':
    // Finished, duplicated, and already excluded from every total. There is
    // nothing for the reader to do about it, which is the point of saying so.
    case 'settled':
    // A session event asks nothing of the reader. The stop reason on the row
    // already says whether anything needs doing.
    case 'lifecycle':
      return null;
  }
}

/**
 * The backend's own sentence, shown when it adds something the explanation
 * cannot — WHICH rule fired, and with what numbers. "Margin cap 10.0% of
 * $10000 = $1000.00, order needs $4000.00 margin" is the difference between a
 * user understanding their own settings and guessing at them.
 *
 * Suppressed where it would only repeat the explanation, and for our own bugs,
 * where an internal string helps nobody.
 */
export function detailFor(order: ReplicatedOrderLike, outcome: Outcome): string | null {
  if (outcome === 'failed') return order.last_error || null;
  if (outcome === 'tooSmallNotSent') return order.last_error || null;
  if (outcome === 'ourFault' || outcome === 'filled' || outcome === 'pending' ||
      outcome === 'working') return null;
  // The signal's own numbers are the message; the backend has no extra sentence.
  if (outcome === 'signalled') return null;
  const reason = order.filter_reason;
  if (!reason) return null;
  // The not-listed and no-position sentences already say it in plain words.
  if (outcome === 'notListed' || outcome === 'noPosition') return null;
  return reason;
}

/****************
 * THE TILES MUST ADD UP TO THE NUMBER BESIDE THEM.
 *
 * They did not, twice. The first time an `execution_status="filtered"` row with
 * `was_filtered=false` landed in no tile; that was fixed by widening a branch.
 * It came back, because widening a branch does not stop the NEXT outcome from
 * falling through — `paused`, `pending` and `lifecycle` all did, while `total`
 * stayed `orders.length` and counted every one of them. A user who paused
 * mid-session saw four numbers that contradicted each other, and every session's
 * lifecycle row inflated the total on its own.
 *
 * So the arithmetic is now structural rather than maintained. Every outcome
 * lands in exactly one bucket, `default` catches anything added to the Outcome
 * union later, and `total` is the SUM of the buckets rather than a second
 * measurement of the same list. A new outcome can no longer go missing quietly:
 * the worst it can do is show up in `other`, which is visible.
 *
 * Two things are deliberately outside `total`, because they are not orders that
 * could have executed:
 *   · `lifecycle` — a session starting or ending. It rides in the order feed
 *     because that is what the notification bell reads. It is not an order.
 *   · `signalled` — delivered to a signal session, which places nothing. It is
 *     neither an execution nor a miss, and counting it as either is the thing
 *     the original comment here was guarding against.
 * Both are still returned, so a caller that wants them can say so explicitly.
 ****************/
export function summariseOutcomes(orders: ReplicatedOrderLike[]) {
  let executed = 0;
  let filtered = 0;
  let failed = 0;
  let pending = 0;
  let signalled = 0;
  let lifecycle = 0;
  // Counted apart from `pending` and `failed`, which is the whole point of the
  // outcome existing. These rows are finished and excluded from every total the
  // session reports, so a tile calling them either of those was telling the user
  // there was something to wait for or something to fix.
  let settled = 0;
  let other = 0;

  for (const order of orders) {
    switch (outcomeOf(order)) {
      case 'filled':
        executed += 1;
        break;

      // Our own bug never reached the broker, but it is not a rule working
      // either — it belongs with the failures, where someone will look at it.
      case 'traderLeftBeforeItFilled':
      case 'acceptedButNeverFilled':
      case 'failed':
      case 'ourFault':
      /*
       * The venue's own refusals belong HERE and not with the filters.
       * They reached the exchange and came back; counting them as Filtered
       * tells the user their configuration turned down a trade that their
       * configuration never saw.
       */
      case 'venueNoMargin':
      case 'venueOrderTooSmall':
      case 'venueStopBeyondLiquidation':
      case 'venueTriggerAlreadyReached':
      case 'venuePaused':
      case 'venueTradingOff':
        failed += 1;
        break;

      // Never reached the broker: the user's rules, the user's funds, or
      // nothing to act on. `paused` belongs here and was the outcome that fell
      // through — a pause is the user deliberately holding, which is as much
      // their own doing as any filter they set.
      case 'strategy':
      case 'notListed':
      case 'noPosition':
      case 'insufficientFunds':
      case 'killSwitch':
      case 'needsSetting':
      case 'paused':
      case 'tooSmallNotSent':
        filtered += 1;
        break;

      // Still in flight. Not a success and not a miss yet, and hiding it makes
      // a busy moment look like a quiet one.
      case 'pending':
        pending += 1;
        break;

      case 'signalled':
        signalled += 1;
        break;

      case 'lifecycle':
        lifecycle += 1;
        break;

      case 'settled':
      // A cancel the user asked for is not a failure anyone needs to look at.
      case 'cancelledByYou':
      case 'nothingToClose':
        settled += 1;
        break;

      default:
        other += 1;
        break;
    }
  }

  return {
    executed,
    filtered,
    failed,
    pending,
    signalled,
    lifecycle,
    settled,
    other,
    // settled is in the total where it has always been -- these rows used to
    // reach `other`, which the total already counted. Splitting them out of
    // `pending`/`failed` must not change how many orders the session says it has.
    total: executed + filtered + failed + pending + settled + other,
  };
}

/**
 * "N missed" was one orange number covering two opposite situations: rules
 * working exactly as set, and an account that has copied nothing for two days.
 * Split so a UI can say which.
 */
export function summariseMissed(orders: ReplicatedOrderLike[]) {
  const { filtered, failed } = summariseOutcomes(orders);
  return { skipped: filtered, failed, needsAttention: failed > 0 };
}
